import React, { useState, useRef, useEffect } from 'react';
import { useConceptGate } from '../../../context/ConceptGateContext';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import SettingsIcon from '@mui/icons-material/Settings';
import { isCodexUi } from '../../../utils/codexEnv';
import PipelineSettingsModal from './PipelineSettingsModal';

/**
 * PipelineSelector component - allows users to switch between validation pipelines
 * and manage them (create, rename, delete, duplicate)
 */
const PipelineSelector = ({ subscriptionStatus }) => {
  const {
    pipelines,
    activePipeline,
    pipelineLoading,
    switchPipeline,
    createNewPipeline,
    updatePipelineInfo,
    deletePipelineById,
    duplicatePipelineById,
  } = useConceptGate();

  const [isOpen, setIsOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [isEditing, setIsEditing] = useState(null); // pipeline id being edited
  const [newPipelineName, setNewPipelineName] = useState('');
  const [editName, setEditName] = useState('');
  const [error, setError] = useState(null);
  const [settingsPipeline, setSettingsPipeline] = useState(null); // pipeline to show settings for
  const dropdownRef = useRef(null);

  // Get pipeline limit from subscription
  const maxPipelines = subscriptionStatus?.maxPipelinesPerInstrument ?? 1;
  const currentCount = pipelines?.length || 0;
  const canCreate = maxPipelines === -1 || currentCount < maxPipelines;

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setIsCreating(false);
        setIsEditing(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = async (pipelineId) => {
    if (pipelineId === activePipeline?.id) {
      setIsOpen(false);
      return;
    }
    await switchPipeline(pipelineId);
    setIsOpen(false);
  };

  const handleCreateClick = () => {
    setIsCreating(true);
    setNewPipelineName('');
    setError(null);
  };

  const handleCreateSubmit = async () => {
    if (!newPipelineName.trim()) {
      setError('Name is required');
      return;
    }

    // Check for duplicate name
    if (pipelines.some(p => p.name.toLowerCase() === newPipelineName.trim().toLowerCase())) {
      setError('A pipeline with this name already exists');
      return;
    }

    const result = await createNewPipeline(newPipelineName.trim());
    if (result) {
      setIsCreating(false);
      setNewPipelineName('');
      setError(null);
    } else {
      setError('Failed to create pipeline');
    }
  };

  const handleEditClick = (e, pipeline) => {
    e.stopPropagation();
    setIsEditing(pipeline.id);
    setEditName(pipeline.name);
    setError(null);
  };

  const handleEditSubmit = async (pipelineId) => {
    if (!editName.trim()) {
      setError('Name is required');
      return;
    }

    // Check for duplicate name (excluding current pipeline)
    if (pipelines.some(p => p.id !== pipelineId && p.name.toLowerCase() === editName.trim().toLowerCase())) {
      setError('A pipeline with this name already exists');
      return;
    }

    const result = await updatePipelineInfo(pipelineId, { name: editName.trim() });
    if (result) {
      setIsEditing(null);
      setEditName('');
      setError(null);
    } else {
      setError('Failed to rename pipeline');
    }
  };

  const handleDeleteClick = async (e, pipelineId) => {
    e.stopPropagation();
    if (pipelineId === activePipeline?.id) {
      setError('Cannot delete the active pipeline');
      return;
    }
    const success = await deletePipelineById(pipelineId);
    if (!success) {
      setError('Failed to delete pipeline');
    }
  };

  const handleDuplicateClick = async (e, pipeline) => {
    e.stopPropagation();
    if (!canCreate) {
      setError(`Pipeline limit reached (${maxPipelines})`);
      return;
    }
    const newName = `${pipeline.name} (copy)`;
    const result = await duplicatePipelineById(pipeline.id, newName);
    if (!result) {
      setError('Failed to duplicate pipeline');
    }
  };

  // Don't render in Codex mode
  if (isCodexUi) {
    return null;
  }

  // Don't render if no pipelines loaded yet
  if (!activePipeline && !pipelineLoading) {
    return null;
  }

  return (
    <div className="pipeline-selector" ref={dropdownRef}>
      <div className="pipeline-selector-row">
        <button
          className={`pipeline-selector-trigger ${isOpen ? 'open' : ''} ${pipelineLoading ? 'loading' : ''}`}
          onClick={() => !pipelineLoading && setIsOpen(!isOpen)}
          disabled={pipelineLoading}
        >
          <span className="pipeline-label">Pipeline:</span>
          <span className="pipeline-name">
            {pipelineLoading ? 'Loading...' : activePipeline?.name || 'Default'}
          </span>
          {activePipeline?.todayRuns > 0 && (
            <span className="pipeline-today-runs" title="Validations run today">
              {activePipeline.todayRuns} runs
            </span>
          )}
          {maxPipelines !== -1 && (
            <span className="pipeline-count">
              ({currentCount}/{maxPipelines})
            </span>
          )}
          <KeyboardArrowDownIcon className={`pipeline-arrow ${isOpen ? 'rotated' : ''}`} />
        </button>
        {activePipeline && (
          <button
            className="pipeline-settings-btn"
            onClick={() => setSettingsPipeline(activePipeline)}
            title="Pipeline Settings"
          >
            <SettingsIcon fontSize="small" />
          </button>
        )}
      </div>

      {isOpen && (
        <div className="pipeline-dropdown">
          {error && (
            <div className="pipeline-dropdown-error">{error}</div>
          )}

          {/* Pipeline list */}
          <div className="pipeline-dropdown-list">
            {pipelines.map((pipeline) => (
              <div
                key={pipeline.id}
                className={`pipeline-dropdown-item ${pipeline.id === activePipeline?.id ? 'active' : ''}`}
                onClick={() => !isEditing && handleSelect(pipeline.id)}
              >
                {isEditing === pipeline.id ? (
                  <div className="pipeline-edit-form" onClick={e => e.stopPropagation()}>
                    <input
                      type="text"
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleEditSubmit(pipeline.id);
                        if (e.key === 'Escape') setIsEditing(null);
                      }}
                      autoFocus
                      className="pipeline-edit-input"
                    />
                    <button
                      className="pipeline-edit-btn save"
                      onClick={() => handleEditSubmit(pipeline.id)}
                      title="Save"
                    >
                      <CheckIcon fontSize="small" />
                    </button>
                    <button
                      className="pipeline-edit-btn cancel"
                      onClick={() => setIsEditing(null)}
                      title="Cancel"
                    >
                      <CloseIcon fontSize="small" />
                    </button>
                  </div>
                ) : (
                  <>
                    <span className="pipeline-item-name">
                      {pipeline.name}
                      <span className="pipeline-item-stats">
                        {pipeline.ruleCount !== undefined && (
                          <span className="pipeline-rule-count">{pipeline.ruleCount} rules</span>
                        )}
                        {pipeline.todayRuns > 0 && (
                          <span className="pipeline-runs-count">{pipeline.todayRuns} runs today</span>
                        )}
                      </span>
                    </span>
                    <div className="pipeline-item-actions">
                      <button
                        className="pipeline-action-btn"
                        onClick={(e) => handleEditClick(e, pipeline)}
                        title="Rename"
                      >
                        <EditIcon fontSize="small" />
                      </button>
                      <button
                        className="pipeline-action-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSettingsPipeline(pipeline);
                          setIsOpen(false);
                        }}
                        title="Settings"
                      >
                        <SettingsIcon fontSize="small" />
                      </button>
                      <button
                        className="pipeline-action-btn"
                        onClick={(e) => handleDuplicateClick(e, pipeline)}
                        title="Duplicate"
                        disabled={!canCreate}
                      >
                        <ContentCopyIcon fontSize="small" />
                      </button>
                      {pipeline.id !== activePipeline?.id && pipelines.length > 1 && (
                        <button
                          className="pipeline-action-btn delete"
                          onClick={(e) => handleDeleteClick(e, pipeline.id)}
                          title="Delete"
                        >
                          <DeleteIcon fontSize="small" />
                        </button>
                      )}
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>

          {/* Create new pipeline */}
          {canCreate && (
            <div className="pipeline-dropdown-footer">
              {isCreating ? (
                <div className="pipeline-create-form">
                  <input
                    type="text"
                    value={newPipelineName}
                    onChange={(e) => setNewPipelineName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleCreateSubmit();
                      if (e.key === 'Escape') setIsCreating(false);
                    }}
                    placeholder="Pipeline name..."
                    autoFocus
                    className="pipeline-create-input"
                  />
                  <button
                    className="pipeline-create-btn save"
                    onClick={handleCreateSubmit}
                    title="Create"
                  >
                    <CheckIcon fontSize="small" />
                  </button>
                  <button
                    className="pipeline-create-btn cancel"
                    onClick={() => setIsCreating(false)}
                    title="Cancel"
                  >
                    <CloseIcon fontSize="small" />
                  </button>
                </div>
              ) : (
                <button className="pipeline-add-btn" onClick={handleCreateClick}>
                  <AddIcon fontSize="small" />
                  <span>New Pipeline</span>
                </button>
              )}
            </div>
          )}

          {!canCreate && (
            <div className="pipeline-dropdown-limit">
              Upgrade to create more pipelines
            </div>
          )}
        </div>
      )}

      {/* Pipeline Settings Modal */}
      <PipelineSettingsModal
        pipeline={settingsPipeline}
        open={!!settingsPipeline}
        onClose={() => setSettingsPipeline(null)}
      />
    </div>
  );
};

export default PipelineSelector;
